import { DataStore } from "@aws-amplify/datastore";
import { Submissions } from "../models";
import { resolveOptionsForField } from "../fields/Fields.js";
import { buildOptionLookup } from "../fields/FieldOptionHelpers.js";

const TOPIC_ALIAS_TO_VALUE = new Map([
  ["METABOLIC", "METABOLIC_PHYSIOLOGY"],
  ["EDU_TEACHING", "EDUCATION_TEACHING"],
  ["CARDIO_VASCULAR", "CARDIAC_VASCULAR"],
]);

const SPECIALITY_ALIAS_TO_VALUE = new Map([
  ["EPITHELIA & MEMBRANE TRANSPORT", "EPITHELIA_MEMBRANE_TRANSPORT_SIG"],
]);

function isBlankValue(value) {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

function normalizeStoredValue(value) {
  return isBlankValue(value) ? null : value;
}

function getSafeLogger(logger) {
  if (logger && typeof logger.info === "function") {
    return logger;
  }

  return {
    info: (...args) => console.log(...args),
    warn: (...args) => console.warn(...args),
    error: (...args) => console.error(...args),
  };
}

export function createAdminJobContext({
  allSubmissions = [],
  currentProject = null,
  currentForm = null,
  assets = [],
  fieldWrapper = null,
  processedFields = [],
  logger = console,
} = {}) {
  return {
    allSubmissions: Array.isArray(allSubmissions) ? allSubmissions : [],
    currentProject,
    currentForm,
    assets: Array.isArray(assets) ? assets : [],
    fieldWrapper,
    processedFields: Array.isArray(processedFields) ? processedFields : [],
    logger: getSafeLogger(logger),
  };
}

export function getAdminJobFields(context) {
  if (Array.isArray(context?.processedFields) && context.processedFields.length > 0) {
    return context.processedFields;
  }

  const wrapperFields =
    typeof context?.fieldWrapper?.getProcessedFields === "function"
      ? context.fieldWrapper.getProcessedFields({ requireBuilt: false })
      : null;

  return Array.isArray(wrapperFields) ? wrapperFields : [];
}

export function findAdminJobField(context, matcher) {
  const fields = getAdminJobFields(context);
  return fields.find((field) => matcher(field)) || null;
}

function buildLookupForField(fieldConfig, label) {
  if (!fieldConfig) {
    throw new Error(`Missing field configuration for ${label}`);
  }

  const resolvedOptions = resolveOptionsForField(fieldConfig);
  const lookup = buildOptionLookup(resolvedOptions);

  if (lookup.values.size === 0) {
    throw new Error(`No options resolved for ${label}`);
  }

  return lookup;
}

function buildCanonicalOptionMap(lookup) {
  const valueToName = new Map();
  const nameToValue = new Map();

  const entries = Array.isArray(lookup?.entries) ? lookup.entries : [];
  for (const entry of entries) {
    const value = typeof entry?.key === "string" ? entry.key.trim() : "";
    const label = typeof entry?.label === "string" ? entry.label.trim() : "";
    if (value) valueToName.set(value, label || value);
    if (label) nameToValue.set(label.toLowerCase(), value || label);
  }

  return { valueToName, nameToValue };
}

export function queryAdminJobSubmissions(context, { projectToken = null, formToken = null } = {}) {
  const source = Array.isArray(context?.allSubmissions) ? context.allSubmissions : [];

  return source.filter((submission) => {
    if (projectToken && submission?.projectToken !== projectToken) return false;
    if (formToken && submission?.formToken !== formToken) return false;
    return true;
  });
}

export async function applySubmissionAdminJob({
  context,
  jobName = "adminJob",
  submissions = [],
  dryRun = true,
  limit = null,
  buildPlan,
  applyPlan,
} = {}) {
  if (typeof buildPlan !== "function") {
    throw new Error(`[${jobName}] buildPlan is required`);
  }

  if (!dryRun && typeof applyPlan !== "function") {
    throw new Error(`[${jobName}] applyPlan is required when dryRun is false`);
  }

  const logger = getSafeLogger(context?.logger);
  let scopedSubmissions = Array.isArray(submissions) ? submissions : [];

  if (typeof limit === "number" && limit > 0) {
    scopedSubmissions = scopedSubmissions.slice(0, limit);
  }

  const summary = {
    jobName,
    dryRun,
    totalScoped: scopedSubmissions.length,
    updated: 0,
    unchanged: 0,
    flaggedInvalid: 0,
    touchedIds: [],
    sample: [],
    invalidExamples: [],
  };

  for (const submission of scopedSubmissions) {
    const plan = buildPlan(submission);
    const hasInvalidFields = Array.isArray(plan?.invalidFields) && plan.invalidFields.length > 0;

    if (!plan?.shouldUpdate && !hasInvalidFields) {
      summary.unchanged += 1;
      continue;
    }

    if (plan?.shouldUpdate) {
      summary.updated += 1;
      summary.touchedIds.push(submission.id);
    }

    if (hasInvalidFields) {
      summary.flaggedInvalid += 1;

      for (const invalidField of plan.invalidFields) {
        if (summary.invalidExamples.length >= 20) break;
        summary.invalidExamples.push({
          id: submission.id,
          trackingNumber: submission?.meta?.trackingNumber ?? null,
          field: invalidField.field,
          value: invalidField.value,
          reason: invalidField.reason,
        });
      }
    }

    if (summary.sample.length < 25) {
      summary.sample.push({
        id: submission.id,
        trackingNumber: submission?.meta?.trackingNumber ?? null,
        before: plan.before,
        after: plan.after,
        actions: plan.actions,
        invalidFields: plan.invalidFields || [],
      });
    }

    if (!dryRun && plan?.shouldUpdate) {
      await applyPlan(submission, plan);
    }
  }

  logger.info(`[AdminJobs] ${jobName} summary`, summary);

  return summary;
}

function resolveSelectFieldValue(currentValue, lookup, fieldLabel) {
  const normalizedValue = normalizeStoredValue(currentValue);

  if (normalizedValue === null) {
    return {
      nextValue: null,
      changed: false,
      matchedOnLabel: false,
      invalid: false,
      invalidValue: null,
      invalidReason: null,
      matchedLabel: null,
    };
  }

  if (lookup.hasValue(normalizedValue)) {
    return {
      nextValue: normalizedValue,
      changed: false,
      matchedOnLabel: false,
      invalid: false,
      invalidValue: null,
      invalidReason: null,
      matchedLabel: null,
    };
  }

  const canonicalMap = buildCanonicalOptionMap(lookup);
  const labelMatch = canonicalMap.nameToValue.get(String(normalizedValue).toLowerCase());

  if (labelMatch) {
    return {
      nextValue: labelMatch,
      changed: labelMatch !== normalizedValue,
      matchedOnLabel: true,
      invalid: false,
      invalidValue: null,
      invalidReason: null,
      matchedLabel: normalizedValue,
    };
  }

  return {
    nextValue: normalizedValue,
    changed: false,
    matchedOnLabel: false,
    invalid: true,
    invalidValue: normalizedValue,
    invalidReason: `${fieldLabel} did not match any allowed value or label`,
    matchedLabel: null,
  };
}

function resolveSelectFieldValueWithAliases(currentValue, lookup, fieldLabel, aliasMap) {
  const normalizedValue = normalizeStoredValue(currentValue);

  if (normalizedValue !== null && aliasMap instanceof Map) {
    const aliasValue = aliasMap.get(String(normalizedValue).trim().toUpperCase());
    if (aliasValue) {
      return {
        nextValue: aliasValue,
        changed: aliasValue !== normalizedValue,
        matchedOnLabel: false,
        invalid: false,
        invalidValue: null,
        invalidReason: null,
        matchedLabel: null,
        matchedOnAlias: true,
      };
    }
  }

  return {
    ...resolveSelectFieldValue(normalizedValue, lookup, fieldLabel),
    matchedOnAlias: false,
  };
}

function resolveTopicValue({ currentTopic, currentTheme, topicLookup }) {
  const normalizedTopic = normalizeStoredValue(currentTopic);
  const normalizedTheme = normalizeStoredValue(currentTheme);

  const applyTopicAlias = (value, sourceLabel) => {
    if (value === null) return null;
    const aliasValue = TOPIC_ALIAS_TO_VALUE.get(String(value).trim().toUpperCase());
    if (!aliasValue) return null;

    return {
      nextValue: aliasValue,
      changed: aliasValue !== value,
      matchedOnLabel: false,
      invalid: false,
      invalidValue: null,
      invalidReason: null,
      matchedLabel: null,
      sourcedFromTheme: sourceLabel === "theme",
      matchedOnAlias: true,
    };
  };

  if (normalizedTopic !== null) {
    const aliasedTopic = applyTopicAlias(normalizedTopic, "topic");
    if (aliasedTopic) return aliasedTopic;

    const resolved = resolveSelectFieldValue(normalizedTopic, topicLookup, "topic");
    return {
      ...resolved,
      sourcedFromTheme: false,
      matchedOnAlias: false,
    };
  }

  if (normalizedTheme !== null) {
    const aliasedTheme = applyTopicAlias(normalizedTheme, "theme");
    if (aliasedTheme) return aliasedTheme;

    const resolved = resolveSelectFieldValue(normalizedTheme, topicLookup, "theme");
    return {
      ...resolved,
      sourcedFromTheme: true,
      matchedOnAlias: false,
    };
  }

  return {
    nextValue: null,
    changed: false,
    matchedOnLabel: false,
    invalid: false,
    invalidValue: null,
    invalidReason: null,
    matchedLabel: null,
    sourcedFromTheme: false,
    matchedOnAlias: false,
  };
}

function buildSubmissionSelectRepairPlan(
  submission,
  topicLookup,
  specialityLookup,
  presentationTypeLookup
) {
  const data = submission?.data && typeof submission.data === "object" ? submission.data : {};
  const currentTopic = data.topic;
  const currentTheme = data.theme;
  const currentSpeciality = data.speciality;
  const currentPresentationType = data.presentation_type;

  const nextTopic = resolveTopicValue({
    currentTopic,
    currentTheme,
    topicLookup,
  });
  const nextSpeciality = resolveSelectFieldValueWithAliases(
    currentSpeciality,
    specialityLookup,
    "speciality",
    SPECIALITY_ALIAS_TO_VALUE
  );
  const nextPresentationType = resolveSelectFieldValue(
    currentPresentationType,
    presentationTypeLookup,
    "presentation_type"
  );

  const themePresent = normalizeStoredValue(currentTheme) !== null;
  const topicResolved = normalizeStoredValue(nextTopic.nextValue) !== null;
  const shouldRemoveTheme = themePresent && topicResolved;

  const topicChanged =
    normalizeStoredValue(currentTopic) !== nextTopic.nextValue ||
    (normalizeStoredValue(currentTopic) === null &&
      normalizeStoredValue(currentTheme) !== null &&
      nextTopic.invalid === false);
  const specialityChanged = nextSpeciality.changed;
  const presentationTypeChanged = nextPresentationType.changed;
  const invalidFields = [
    nextTopic.invalid
      ? {
          field: "topic",
          value: nextTopic.invalidValue,
          reason: nextTopic.invalidReason,
        }
      : null,
    nextSpeciality.invalid
      ? {
          field: "speciality",
          value: nextSpeciality.invalidValue,
          reason: nextSpeciality.invalidReason,
        }
      : null,
    nextPresentationType.invalid
      ? {
          field: "presentation_type",
          value: nextPresentationType.invalidValue,
          reason: nextPresentationType.invalidReason,
        }
      : null,
  ].filter(Boolean);

  return {
    shouldUpdate: topicChanged || specialityChanged || presentationTypeChanged || shouldRemoveTheme,
    actions: {
      normalizedTopicFromAlias: nextTopic.matchedOnAlias,
      normalizedTopicFromLabel: nextTopic.matchedOnLabel,
      populatedTopicFromTheme: nextTopic.sourcedFromTheme && nextTopic.invalid === false,
      removedTheme: shouldRemoveTheme,
      normalizedSpecialityFromAlias: nextSpeciality.matchedOnAlias,
      normalizedSpecialityFromLabel: nextSpeciality.matchedOnLabel,
      normalizedPresentationTypeFromLabel: nextPresentationType.matchedOnLabel,
      hasInvalidFields: invalidFields.length > 0,
    },
    before: {
      topic: currentTopic ?? null,
      theme: currentTheme ?? null,
      speciality: currentSpeciality ?? null,
      presentation_type: currentPresentationType ?? null,
    },
    after: {
      topic: nextTopic.nextValue,
      speciality: nextSpeciality.nextValue,
      presentation_type: nextPresentationType.nextValue,
    },
    invalidFields,
  };
}

export async function repairSubmissionSelectFieldData(
  context,
  {
    submissions = null,
    projectToken = context?.currentProject?.token || null,
    formToken = context?.currentForm?.token || null,
    dryRun = true,
    limit = null,
  } = {}
) {
  const topicField = findAdminJobField(
    context,
    (field) => field?.dataSource === "data.topic" || field?.field === "topic"
  );
  const specialityField = findAdminJobField(
    context,
    (field) =>
      field?.dataSource === "data.speciality" ||
      field?.field === "speciality" ||
      field?.field === "speciality2"
  );
  const presentationTypeField = findAdminJobField(
    context,
    (field) =>
      field?.dataSource === "data.presentation_type" ||
      field?.field === "presentation_type"
  );

  const topicLookup = buildLookupForField(topicField, "data.topic");
  const specialityLookup = buildLookupForField(specialityField, "data.speciality");
  const presentationTypeLookup = buildLookupForField(
    presentationTypeField,
    "data.presentation_type"
  );

  const scopedSubmissions = Array.isArray(submissions)
    ? submissions
    : queryAdminJobSubmissions(context, { projectToken, formToken });

  const summary = await applySubmissionAdminJob({
    context,
    jobName: "repairSubmissionSelectFieldData",
    submissions: scopedSubmissions,
    dryRun,
    limit,
    buildPlan: (submission) =>
      buildSubmissionSelectRepairPlan(
        submission,
        topicLookup,
        specialityLookup,
        presentationTypeLookup
      ),
    applyPlan: async (submission, plan) => {
      await DataStore.save(
        Submissions.copyOf(submission, (draft) => {
          draft.data = draft.data || {};
          draft.data.topic = plan.after.topic;
          draft.data.speciality = plan.after.speciality;
          draft.data.presentation_type = plan.after.presentation_type;
          if (plan.actions.removedTheme) {
            delete draft.data.theme;
          }
        })
      );
    },
  });

  summary.removedTheme = summary.sample.filter(
    (item) => item.actions?.removedTheme
  ).length;
  summary.normalizedTopicFromAlias = summary.sample.filter(
    (item) => item.actions?.normalizedTopicFromAlias
  ).length;
  summary.populatedTopicFromTheme = summary.sample.filter(
    (item) => item.actions?.populatedTopicFromTheme
  ).length;
  summary.normalizedTopicFromLabel = summary.sample.filter(
    (item) => item.actions?.normalizedTopicFromLabel
  ).length;
  summary.normalizedSpecialityFromLabel = summary.sample.filter(
    (item) => item.actions?.normalizedSpecialityFromLabel
  ).length;
  summary.normalizedSpecialityFromAlias = summary.sample.filter(
    (item) => item.actions?.normalizedSpecialityFromAlias
  ).length;
  summary.normalizedPresentationTypeFromLabel = summary.sample.filter(
    (item) => item.actions?.normalizedPresentationTypeFromLabel
  ).length;
  summary.flaggedInvalid = summary.sample.filter((item) => item.actions?.hasInvalidFields).length;

  if (summary.updated > summary.sample.length) {
    let removedTheme = 0;
    let normalizedTopicFromAlias = 0;
    let populatedTopicFromTheme = 0;
    let normalizedTopicFromLabel = 0;
    let normalizedSpecialityFromAlias = 0;
    let normalizedSpecialityFromLabel = 0;
    let normalizedPresentationTypeFromLabel = 0;
    let flaggedInvalid = 0;

    for (const submission of scopedSubmissions.slice(
      0,
      typeof limit === "number" && limit > 0 ? limit : scopedSubmissions.length
    )) {
      const plan = buildSubmissionSelectRepairPlan(
        submission,
        topicLookup,
        specialityLookup,
        presentationTypeLookup
      );
      if (!plan.shouldUpdate) continue;
      if (plan.actions.removedTheme) removedTheme += 1;
      if (plan.actions.normalizedTopicFromAlias) normalizedTopicFromAlias += 1;
      if (plan.actions.populatedTopicFromTheme) populatedTopicFromTheme += 1;
      if (plan.actions.normalizedTopicFromLabel) normalizedTopicFromLabel += 1;
      if (plan.actions.normalizedSpecialityFromAlias) normalizedSpecialityFromAlias += 1;
      if (plan.actions.normalizedSpecialityFromLabel) normalizedSpecialityFromLabel += 1;
      if (plan.actions.normalizedPresentationTypeFromLabel) {
        normalizedPresentationTypeFromLabel += 1;
      }
    }

    for (const submission of scopedSubmissions.slice(
      0,
      typeof limit === "number" && limit > 0 ? limit : scopedSubmissions.length
    )) {
      const plan = buildSubmissionSelectRepairPlan(
        submission,
        topicLookup,
        specialityLookup,
        presentationTypeLookup
      );
      if (plan.actions.hasInvalidFields) flaggedInvalid += 1;
    }

    summary.removedTheme = removedTheme;
    summary.normalizedTopicFromAlias = normalizedTopicFromAlias;
    summary.populatedTopicFromTheme = populatedTopicFromTheme;
    summary.normalizedTopicFromLabel = normalizedTopicFromLabel;
    summary.normalizedSpecialityFromAlias = normalizedSpecialityFromAlias;
    summary.normalizedSpecialityFromLabel = normalizedSpecialityFromLabel;
    summary.normalizedPresentationTypeFromLabel = normalizedPresentationTypeFromLabel;
    summary.flaggedInvalid = flaggedInvalid;
  }

  return summary;
}

export async function repairSubmissionTopicAndSpecialityData(context, options = {}) {
  return repairSubmissionSelectFieldData(context, options);
}

export const AdminSubmissionJobs = Object.freeze({
  repairSubmissionTopicAndSpecialityData,
  repairSubmissionSelectFieldData,
});

export default {
  createAdminJobContext,
  getAdminJobFields,
  findAdminJobField,
  queryAdminJobSubmissions,
  applySubmissionAdminJob,
  repairSubmissionTopicAndSpecialityData,
  repairSubmissionSelectFieldData,
  AdminSubmissionJobs,
};
